<script setup lang="ts">
import { format } from "date-fns/format";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type TenantDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";

const api = useApi();
const router = useRouter();
const isLoading = ref(true);
const tenants = ref<TenantDto[]>([]);

async function refreshTenants() {
  isLoading.value = true;
  tenants.value = await api.listTenants();
  isLoading.value = false;
}

function formatDatetime(dt: string) {
  return format(new Date(dt), "d MMMM yyyy");
}

function onEdit(tenantId: number) {
  router.push({ name: RouteNames.TenantDetail, params: { tenantId } });
}

onMounted(async () => {
  await refreshTenants();
  if (tenants.value.length == 1) {
    const tenant = tenants.value[0];
    router.replace({ name: RouteNames.Projects, params: { tenantName: tenant.name } });
  }
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="[{ label: 'Tenants' }]">
    <div class="tenants-list-view">
      <Transition name="fade" mode="out-in">
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else-if="tenants.length > 0" class="wrapper">
          <header>
            <h6>Tenants</h6>
            <div class="paragraph-s">Browse the tenants you have access to.</div>
          </header>
          <table class="tabular-list tenants">
            <thead>
              <tr>
                <th>Tenant name</th>
                <th>Members</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tenant in tenants" :key="tenant.id">
                <td>
                  <RouterLink
                    :to="{
                      name: RouteNames.Projects,
                      params: { tenantName: tenant.name },
                    }"
                  >
                    <span class="name label-s">
                      {{ tenant.name }}
                    </span>
                  </RouterLink>
                </td>
                <td>{{ tenant.members?.length ?? 0 }}</td>
                <td class="cell-with-actions">
                  <div>{{ formatDatetime(tenant.created_at) }}</div>
                  <DropdownButton icon-right="icon-more-vertical" kind="tertiary">
                    <DropdownButtonItem
                      label="Edit"
                      icon-left="icon-edit"
                      @click="onEdit(tenant.id)"
                    />
                  </DropdownButton>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>You have access to the following tenants:</div>
      </Transition>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.tenants-list-view {
  .loading {
    margin-top: 40dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--spacing-32) - var(--spacing-12)) 0 var(--spacing-32) 0;
    gap: var(--spacing-32);

    header {
      div {
        color: var(--color-text-secondary);
      }
    }

    .cell-with-actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    a {
      text-decoration: none;
    }

    .name {
      --icon-size: 28px;

      position: relative;
      padding-left: calc(var(--icon-size) + var(--spacing-8));
      color: var(--color-text-primary);

      &::before {
        position: absolute;
        top: -35%;
        left: 0;
        overflow: hidden;
        width: var(--icon-size);
        height: var(--icon-size);
        border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
        border-radius: var(--icon-size);
        background-image: url("@/assets/images/skore-icon.svg");
        background-position: 60% 50%;
        background-repeat: no-repeat;
        background-size: 70%;
        content: "";
      }
    }
  }
}
</style>
